<template lang="pug">
el-form.admin-form(
  ref="formRef",
  :model="model",
  :rules="rules",
  :disabled="disabled",
  :label-position="labelPosition",
  :label-width="labelWidth",
  :validate-on-rule-change="validateOnRuleChange",
  @submit.prevent="submit",
  @validate="onValidate",
  require-asterisk-position="right"
)
  slot
  AdminFormErrorSummary(
    v-if="hasSubmissionErrors && showInlineErrorSummary",
    ref="summaryRef",
    :errors="submissionErrors",
    :title="$t('fixErrorsBeforeSaving')",
    @select="focusField"
  )
  slot(name="actions")
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from "element-plus";
import { computed, nextTick, provide, ref } from "vue";
import AdminFormErrorSummary from "./AdminFormErrorSummary.vue";
import {
  adminFormContextKey,
  type ExternalFormErrors,
  type SubmissionError,
} from "./types";

const props = withDefaults(
  defineProps<{
    model: Record<string, unknown>;
    rules?: FormRules;
    externalErrors?: ExternalFormErrors;
    disabled?: boolean;
    labelPosition?: "left" | "right" | "top";
    labelWidth?: string | number;
    validateOnRuleChange?: boolean;
    showInlineErrorSummary?: boolean;
  }>(),
  {
    rules: () => ({}),
    externalErrors: () => ({}),
    disabled: false,
    labelPosition: "left",
    labelWidth: "20ch",
    validateOnRuleChange: false,
    showInlineErrorSummary: true,
  },
);

const emit = defineEmits<{
  submit: [];
  invalidSubmit: [errors: ExternalFormErrors];
  validate: [prop: string, valid: boolean, message: string];
}>();

const formRef = ref<FormInstance>();
const summaryRef = ref<InstanceType<typeof AdminFormErrorSummary>>();
const validationErrors = ref<ExternalFormErrors>({});
const submitted = ref(false);

const externalErrors = computed(() => props.externalErrors);
const formDisabled = computed(() => props.disabled);

const allErrors = computed<ExternalFormErrors>(() => ({
  ...validationErrors.value,
  ...externalErrors.value,
}));

provide(adminFormContextKey, {
  externalErrors: allErrors,
  disabled: formDisabled,
});

const submissionErrors = computed<SubmissionError[]>(() =>
  Object.entries(allErrors.value)
    .filter((entry): entry is [string, string] => entry[1] !== undefined)
    .map(([prop, message]) => ({ prop, message })),
);

const hasSubmissionErrors = computed(
  () => submitted.value && submissionErrors.value.length > 0,
);

function fieldElementId(prop: string): string {
  return `admin-form-field-${prop.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function onValidate(prop: string, valid: boolean, message: string): void {
  if (valid) {
    validationErrors.value = Object.fromEntries(
      Object.entries(validationErrors.value).filter(
        ([errorProp]) => errorProp !== prop,
      ),
    );
  } else {
    validationErrors.value = { ...validationErrors.value, [prop]: message };
  }
  emit("validate", prop, valid, message);
}

async function validate(): Promise<boolean> {
  const isValid = await formRef.value?.validate().catch(() => false);
  return isValid ?? true;
}

async function focusField(prop: string): Promise<void> {
  await nextTick();
  const field = document.getElementById(fieldElementId(prop));
  const focusTarget = field?.querySelector<HTMLElement>(
    "input, select, textarea, button, [tabindex]:not([tabindex='-1'])",
  );
  focusTarget?.focus();
}

async function focusFirstInvalid(): Promise<void> {
  await nextTick();
  summaryRef.value?.focus();
  const firstInvalidControl = document.querySelector<HTMLElement>(
    ".admin-form .is-error input, .admin-form .is-error select, .admin-form .is-error textarea, .admin-form .is-error button, .admin-form .is-error [tabindex]:not([tabindex='-1'])",
  );
  if (firstInvalidControl) {
    firstInvalidControl.focus();
    return;
  }

  const firstError = submissionErrors.value[0];
  if (!firstError) {
    return;
  }
  await focusField(firstError.prop);
}

async function submit(): Promise<void> {
  submitted.value = true;
  const isValid = await validate();

  if (isValid && submissionErrors.value.length === 0) {
    emit("submit");
    return;
  }

  emit("invalidSubmit", allErrors.value);
  await focusFirstInvalid();
}

function clearValidate(props?: string | string[]): void {
  formRef.value?.clearValidate(props);
  clearValidationErrors(props);
}

function clearValidationErrors(props?: string | string[]): void {
  if (!props) {
    validationErrors.value = {};
    return;
  }
  const properties = Array.isArray(props) ? props : [props];
  validationErrors.value = Object.fromEntries(
    Object.entries(validationErrors.value).filter(
      ([prop]) => !properties.includes(prop),
    ),
  );
}

function resetFields(props?: string | string[]): void {
  formRef.value?.resetFields(props);
  clearValidationErrors(props);
}

function validateField(props: string | string[]): Promise<boolean> {
  return (
    formRef.value
      ?.validateField(props)
      .then(() => true)
      .catch(() => false) ?? Promise.resolve(true)
  );
}

function scrollToField(prop: string): void {
  formRef.value?.scrollToField(prop);
}

defineExpose({
  submit,
  validate,
  validateField,
  clearValidate,
  resetFields,
  scrollToField,
  focusFirstInvalid,
  focusField,
  hasSubmissionErrors,
  submissionErrors,
});
</script>
